import {
  MeetingLimitReset,
  MeetingLimitSetMeetingLimit,
  PublicMeetingType,
} from '@chilipiper/api-client/src/meeting-types'
import { ScheduleOrigin } from '@chilipiper/api-client/src/meetings'
import {
  MeetingLimit,
  PublicAdminSchedulingLink,
  PublicGroupSchedulingLink,
} from '@chilipiper/api-client/src/team-scheduling'
import {
  LinkSourceDiscriminator,
  AdminSchedulingLink as AdminSchedulingLinkLinkSource,
  PersonalSchedulingLink as PersonalSchedulingLinkLinkSource,
  GroupSchedulingLink as GroupSchedulingLinkLinkSource,
  NoMeetingLimit,
  SetMeetingLimit,
} from '@chilipiper/api-client/src/user-availability'

const isMeetingLimitSetMeetingLimit = (
  limit?: MeetingLimitSetMeetingLimit | MeetingLimit
): limit is MeetingLimitSetMeetingLimit => {
  return limit ? 'numberOfMeetings' in limit : false
}

export type NomalizedMeetingHostLimit =
  | {
      numberOfMeetings: number
      reset: MeetingLimitReset
      type: 'SetMeetingLimit'
    }
  | {
      type: 'NoMeetingLimit'
    }

const normalizeMeetingHostLimit = (
  limit?: MeetingLimitSetMeetingLimit | MeetingLimit
): NomalizedMeetingHostLimit => {
  if (isMeetingLimitSetMeetingLimit(limit)) {
    return {
      numberOfMeetings: limit.numberOfMeetings,
      reset: limit.interval,
      type: limit.type,
    }
  }

  if (limit?.type === 'SetMeetingLimit') {
    return {
      numberOfMeetings: limit.maximumMeetingsCount,
      reset: limit.reset as MeetingLimitReset,
      type: limit.type,
    }
  }

  return {
    type: 'NoMeetingLimit',
  }
}

type LinkSource =
  | AdminSchedulingLinkLinkSource
  | PersonalSchedulingLinkLinkSource
  | GroupSchedulingLinkLinkSource

const getHostMeetingLimitSource = (
  type?: LinkSourceDiscriminator,
  id?: string
): LinkSource | undefined => {
  if (type === 'AdminSchedulingLink' && id) {
    return {
      type: 'AdminSchedulingLink',
      adminSchedulingLinkId: id,
    }
  }

  if (type === 'PersonalSchedulingLink' && id) {
    return {
      type: 'PersonalSchedulingLink',
      meetingTypeId: id,
    }
  }

  if (type === 'GroupSchedulingLink' && id) {
    return {
      type: 'GroupSchedulingLink',
      groupSchedulingLinkId: id,
    }
  }

  return undefined
}

export type MeetingLimitSource = SetMeetingLimit | NoMeetingLimit

const getHostMeetingLimit = (
  hostMeetingLimit?: NomalizedMeetingHostLimit,
  sourceType?: LinkSourceDiscriminator,
  id?: string
): MeetingLimitSource => {
  const source = getHostMeetingLimitSource(sourceType, id)

  if (hostMeetingLimit && hostMeetingLimit?.type !== 'NoMeetingLimit' && source) {
    return {
      source,
      maximumMeetingsCount: hostMeetingLimit.numberOfMeetings,
      reset: hostMeetingLimit.reset,
      type: hostMeetingLimit.type,
    }
  }

  return {
    type: 'NoMeetingLimit',
  }
}

type MeetingLimitType = 'AdminSchedulingLink' | 'PersonalSchedulingLink' | 'GroupSchedulingLink'

export const getMeetingLimit = ({
  linkInfo,
  type,
  id,
}: {
  linkInfo?: PublicMeetingType | PublicAdminSchedulingLink | PublicGroupSchedulingLink | null
  type?: MeetingLimitType
  id?: string
}): MeetingLimitSource => {
  const normalizedMeetingHostLimit = normalizeMeetingHostLimit(getMeetingLimitFromSource(linkInfo))

  return getHostMeetingLimit(normalizedMeetingHostLimit, type, id)
}

const getMeetingLimitFromSource = (
  linkInfo?: PublicMeetingType | PublicAdminSchedulingLink | PublicGroupSchedulingLink | null
) => {
  if (linkInfo && 'hostMeetingLimit' in linkInfo) {
    return linkInfo.hostMeetingLimit
  }
}

type LinkInformation = {
  type: 'AdminSchedulingLink' | 'PersonalSchedulingLink' | 'GroupSchedulingLink'
  id: string
}

export const getLinkInformationFromScheduleOrigin = (
  schedulingOrigin?: ScheduleOrigin
): LinkInformation | undefined => {
  if (
    schedulingOrigin?.type === 'Collected' &&
    schedulingOrigin.productFeature.type === 'AdminIndividualSchedulingLink'
  ) {
    return {
      type: 'AdminSchedulingLink',
      id: schedulingOrigin.productFeature.linkId,
    }
  }

  if (
    schedulingOrigin?.type === 'Collected' &&
    schedulingOrigin.productFeature.type === 'PersonalIndividualSchedulingLink'
  ) {
    return {
      type: 'PersonalSchedulingLink',
      id: schedulingOrigin.productFeature.meetingTypeId,
    }
  }

  if (
    schedulingOrigin?.type === 'Collected' &&
    schedulingOrigin.productFeature.type === 'GroupSchedulingLink'
  ) {
    return {
      type: 'GroupSchedulingLink',
      id: schedulingOrigin.productFeature.linkId,
    }
  }

  return undefined
}

export const getLinkInfo = ({
  type,
  groupSchedulingLink,
  meetingType,
  adminSchedulingLink,
}: {
  type?: MeetingLimitType
  meetingType?: PublicMeetingType
  adminSchedulingLink?: PublicAdminSchedulingLink | null
  groupSchedulingLink?: PublicGroupSchedulingLink
}) => {
  const linkInfo = {
    PersonalSchedulingLink: meetingType,
    AdminSchedulingLink: adminSchedulingLink,
    GroupSchedulingLink: groupSchedulingLink,
  }

  if (!type) {
    return
  }

  return linkInfo[type]
}
